// One method per portal menu route (backend/routes/client_portal_menu_routes.py). The client is the portal session's,
// so no URL names it. Bodies and answers pass through as they are: the portal client converts no keys, so allergen ids
// such as frutta_a_guscio reach the server and come back unchanged.
import type { HttpClient } from "@/core/http/types";
import type {
  CategoryAnswer, CategoryOrder, CategoryOrderAnswer, CategoryPatch, DeletedAnswer, ItemAnswer, ItemPatch, MenuDetail,
  MenuList, MenuVersion, NewCategory, NewItem,
} from "../types";

const BASE = "/api/client-portal/menus";
const menuUrl = (menuId: string) => `${BASE}/${encodeURIComponent(menuId)}`;
const itemUrl = (menuId: string, itemId: string) => `${menuUrl(menuId)}/items/${encodeURIComponent(itemId)}`;

export const menuService = {
  /** Answers 404 for a client with menus off, which is how the portal knows to hide Menu. */
  list(http: HttpClient): Promise<MenuList> {
    return http.get<MenuList>(BASE);
  },
  menu(http: HttpClient, menuId: string): Promise<MenuDetail> {
    return http.get<MenuDetail>(menuUrl(menuId));
  },
  async version(http: HttpClient, menuId: string, versionId: string): Promise<MenuVersion> {
    return (await http.get<{ version: MenuVersion }>(`${menuUrl(menuId)}/versions/${encodeURIComponent(versionId)}`)).version;
  },
  async publish(http: HttpClient, menuId: string): Promise<MenuVersion> {
    return (await http.post<{ version: MenuVersion }>(`${menuUrl(menuId)}/publish`)).version;
  },
  createItem(http: HttpClient, menuId: string, body: NewItem): Promise<ItemAnswer> {
    return http.post<ItemAnswer>(`${menuUrl(menuId)}/items`, body);
  },
  patchItem(http: HttpClient, menuId: string, itemId: string, patch: ItemPatch): Promise<ItemAnswer> {
    return http.patch<ItemAnswer>(itemUrl(menuId, itemId), patch);
  },
  deleteItem(http: HttpClient, menuId: string, itemId: string): Promise<DeletedAnswer> {
    return http.delete<DeletedAnswer>(itemUrl(menuId, itemId));
  },
  restoreItem(http: HttpClient, menuId: string, itemId: string): Promise<ItemAnswer> {
    return http.post<ItemAnswer>(`${itemUrl(menuId, itemId)}/restore`);
  },
  duplicateItem(http: HttpClient, menuId: string, itemId: string): Promise<ItemAnswer> {
    return http.post<ItemAnswer>(`${itemUrl(menuId, itemId)}/duplicate`);
  },
  /** Every live item of the category, each once, in its new order. */
  reorderItems(http: HttpClient, menuId: string, categoryId: string, itemIds: string[]): Promise<{ pending: number }> {
    return http.put<{ pending: number }>(`${menuUrl(menuId)}/categories/${encodeURIComponent(categoryId)}/order`, { itemIds });
  },
  /** Last among its siblings. */
  createCategory(http: HttpClient, menuId: string, body: NewCategory): Promise<CategoryAnswer> {
    return http.post<CategoryAnswer>(`${menuUrl(menuId)}/categories`, body);
  },
  patchCategory(http: HttpClient, menuId: string, categoryId: string, patch: CategoryPatch): Promise<CategoryAnswer> {
    return http.patch<CategoryAnswer>(`${menuUrl(menuId)}/categories/${encodeURIComponent(categoryId)}`, patch);
  },
  reorderCategories(http: HttpClient, menuId: string, order: CategoryOrder): Promise<CategoryOrderAnswer> {
    return http.put<CategoryOrderAnswer>(`${menuUrl(menuId)}/categories/order`, order);
  },
};
